import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './App.css'; // 引入 CSS

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

// --- Helper Functions ---

const removeFootnoteMarkers = (text) => {
  if (typeof text !== 'string') return text;
  // 正則表達式保持不變 - 但 poem display 會自己處理標記，所以此函數主要用於 AI 的 markdown 回覆
  return text.replace(/(?<=[\p{Script=Han}\p{P}】])([a-z]|[@#]\d+)(?=\s|[\p{P}]|$)/gu, '');
};

const parseMarkdownTable = (markdown) => {
    if (typeof markdown !== 'string') return null;
    const lines = markdown.trim().split('\n').map(line => line.trim()).filter(line => line);
    if (lines.length < 2) return null;
    let separatorIndex = -1;
    for (let i = 1; i < lines.length; i++) {
        if (lines[i].includes('|') && lines[i].includes('---')) {
             const cells = lines[i].split('|').map(c => c.trim());
             if (cells[0] === '') cells.shift();
             if (cells[cells.length - 1] === '') cells.pop();
             if (cells.length > 0 && cells.every(cell => cell.match(/^:?-+:?$/))) {
                 separatorIndex = i;
                 break;
             }
        }
    }
    if (separatorIndex === -1 || separatorIndex === 0) return null;
    const headerLine = lines[separatorIndex - 1];
    const dataLines = lines.slice(separatorIndex + 1);
    const parseRow = (rowString) => {
        const trimmed = rowString.trim();
        const core = trimmed.startsWith('|') && trimmed.endsWith('|') ? trimmed.slice(1, -1) : trimmed;
        return core.split('|').map(cell => cell.trim());
    };
    const columns = parseRow(headerLine);
    const columnCount = columns.length;
    if (columnCount === 0) return null;
    const rows = [];
    for (let i = 0; i < dataLines.length; i++) {
        const line = dataLines[i];
        if (line.includes('|')) {
            const rowCells = parseRow(line);
            while (rowCells.length < columnCount) rowCells.push('');
            if (rowCells.length > columnCount) rowCells.length = columnCount;
            rows.push(rowCells);
        } else {
           break;
        }
    }
    return { columns, rows };
};

// Updated findTextData to return the full item object
const findTextData = (fullData, category, term) => {
    if (!fullData || !Array.isArray(fullData) || !term || category !== 'titles') return null;
    term = String(term).trim();

    for (const book of fullData) {
        const processItem = (item) => {
            if (!item) return null;
            // Match against various possible title fields
            let itemTitle = String(item.title || item.book_title || item.activity_title || '').trim();
            if (itemTitle === term) {
                return item; // Return the whole item object
            }
            // Recursive search within nested structures if needed (e.g., sub-sections)
            // Example: if (item.sub_items) { ... recursive call ... }
            return null;
        };

        // Search in table_of_contents
        if (book.table_of_contents && Array.isArray(book.table_of_contents)) {
            for (const unit of book.table_of_contents) {
                if (unit && unit.content && Array.isArray(unit.content)) {
                    for (const contentItem of unit.content) {
                         // Use a temporary variable to avoid confusion with the outer 'item'
                        const foundContentItem = processItem(contentItem);
                        if (foundContentItem) return foundContentItem;
                    }
                }
                 // Check unit itself (if units can have titles matching the term)
                 const foundUnitItem = processItem(unit);
                 if (foundUnitItem) return foundUnitItem;
            }
        }
        // Search in supplementary_content
        if (book.supplementary_content && Array.isArray(book.supplementary_content)) {
            for (const supSection of book.supplementary_content) {
                if (supSection && supSection.items && Array.isArray(supSection.items)){
                    for (const supItem of supSection.items) {
                         // Use a temporary variable
                        const foundSupItem = processItem(supItem);
                        if (foundSupItem) return foundSupItem;
                    }
                }
                 // Check section itself
                 const foundSupSectionItem = processItem(supSection);
                 if (foundSupSectionItem) return foundSupSectionItem;
            }
        }
         // Check book object itself (if the book metadata has a matching title)
         // Assuming metadata might contain title, adjust if needed
         if (book.metadata) {
            const foundBookItem = processItem(book.metadata);
            if (foundBookItem) return book.metadata; // Return metadata if it matches
         }
    }
    console.warn(`findTextData: Did not find an exact match for title "${term}"`);
    return null;
};


// --- ChartMessage 和 TableMessage 組件 (無變動) ---
function ChartMessage({ type, chartData }) {
     const data = { labels: chartData.labels || [], datasets: [ { label: chartData.label || '数据', data: chartData.data || [], backgroundColor: type === 'bar' ? 'rgba(143, 154, 111, 0.6)' : 'rgba(143, 154, 111, 0.2)', borderColor: 'rgba(143, 154, 111, 1)', borderWidth: 1, fill: type === 'line', tension: 0.1 }] };
     const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top', labels: { color: 'var(--ghibli-text-dark)' } }, title: { display: !!chartData.title, text: chartData.title, color: 'var(--ghibli-text-dark)' }, tooltip: { backgroundColor: 'rgba(90, 79, 74, 0.8)', titleColor: '#fff', bodyColor: '#fff', } }, scales: { y: { beginAtZero: true, ticks: { color: 'var(--ghibli-text-soft)' }, grid: { color: 'var(--ghibli-border-color)' } }, x: { ticks: { color: 'var(--ghibli-text-soft)' }, grid: { color: 'rgba(0, 0, 0, 0.05)' } } } };
     return (<div className="ai-chart-container"> {type === 'bar' ? <Bar data={data} options={options} /> : <Line data={data} options={options} />} </div>);
 }

function TableMessage({ tableData }) {
    if (!tableData || !Array.isArray(tableData.columns) || !Array.isArray(tableData.rows)) { return <div className="ai-message">無效的表格數據格式</div>; }
    const validRows = tableData.rows.filter(row => Array.isArray(row) && row.some(cell => cell != null && String(cell).trim() !== ''));
    if (tableData.columns.length === 0) {
         return <div className="ai-message">表格標頭為空或格式不正確</div>;
    }
    return (
        <div className="ai-table-container">
            <table className="ai-table">
                <thead> <tr> {tableData.columns.map((col, idx) => <th key={idx}>{col != null ? String(col) : ''}</th>)} </tr> </thead>
                <tbody>
                    {validRows.length > 0 ? (
                        validRows.map((row, rowIdx) => (
                           <tr key={rowIdx}>
                               {Array.isArray(row) ? row.slice(0, tableData.columns.length).map((cell, cellIdx) => (
                                   <td key={cellIdx}><ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: 'span' }}>{cell != null ? String(cell) : ''}</ReactMarkdown></td>
                               )) : <td colSpan={tableData.columns.length}>無效行數據</td>}
                           </tr>
                        ))
                     ) : (
                         <tr><td colSpan={tableData.columns.length} style={{ textAlign: 'center', fontStyle: 'italic' }}>没有数据行</td></tr>
                    )}
                 </tbody>
            </table>
        </div>
    );
}

// --- Thinking Indicator Component (無變動) ---
function ThinkingIndicator() {
  const [dots, setDots] = useState('.');
  useEffect(() => {
    const intervalId = setInterval(() => { setDots(prevDots => prevDots.length >= 3 ? '.' : prevDots + '.'); }, 400);
    return () => clearInterval(intervalId);
  }, []);
  return ( <div className="thinking-message"> thiiiiiiinking{dots} </div> );
}

// --- Sidebar 組件 (MODIFIED) ---
function Sidebar({ data, expandedIndex, onBookClick, onTextClick }) {
  if (!data) {
    return <div className="sidebar-loading">加载目录中...</div>;
  }

  return (
    <div className="sidebar-container">
      {/* Map through each book section */}
      {data.map((section, index) => (
        <div key={section.bookTitle + index} className="sidebar-book-section"> {/* Each section now has a class for :nth-of-type styling */}
          <button
            className={`sidebar-book-item ${expandedIndex === index ? 'expanded' : ''}`}
            onClick={() => onBookClick(index)}
            aria-expanded={expandedIndex === index}
            title={section.bookTitle} // Use the full prefixed title for tooltip/debugging
          >
            {/* NEW: Color Indicator Span */}
            <span className="book-color-indicator" aria-hidden="true"></span>
            {/* Text Span */}
            <span className="sidebar-book-item-text">{section.displayTitle}</span>
            {/* REMOVED: Expand Icon Span */}
            {/* <span className="expand-icon" aria-hidden="true">{expandedIndex === index ? '−' : '+'}</span> */}
          </button>

          {expandedIndex === index && (
            <ul className="sidebar-text-list">
              {section.items.map((item, itemIndex) => (
                <li key={`${item.value}-${item.type}-${itemIndex}`} className={`sidebar-item-${item.type}`}>
                   {item.isClickable ? (
                      <button
                         className="sidebar-text-item"
                         onClick={() => onTextClick(item.value)} // Pass original title value
                         title={item.display}
                      >
                        {item.display}
                      </button>
                    ) : (
                      <span className="sidebar-header-item">
                          {item.display}
                      </span>
                    )}
                </li>
              ))}
               {section.items.length === 0 && <li className="sidebar-no-items">(暂无篇目)</li>}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}


// --- PoemDisplay Component (Unchanged Logic, CSS handles centering/size) ---
function PoemDisplay({ data }) {
    if (!data || typeof data !== 'object') {
        return <div className="poem-display-container poem-error">無法顯示內容：數據無效</div>;
    }

    // Extract data, providing defaults for missing fields
    const {
        title = "无标题",
        author = "未知作者",
        genre = "", // Genre might be optional
        main_text = "无正文内容",
        source_info = "", // Optional
        footnotes = [], // Default to empty array
        learning_tips = "", // Optional
        // recitation_required = false, // We hide false values anyway
        // self_study = false,          // We hide false values anyway
    } = data;

    // *** NEW/UPDATED: Clean main_text using the more robust regex ***
    // This regex handles single letters [a-z] AND patterns like @7, #0
    // (?<=...) is lookbehind (checks char before marker)
    // (?=...) is lookahead (checks char after marker)
    // \p{Script=Han} matches Chinese characters
    // \p{P} matches punctuation
    // | is OR
    // \d+ matches one or more digits
    const cleaned_main_text = typeof main_text === 'string'
        ? main_text.replace(/(?<=[\p{Script=Han}\p{P}】])([a-z]|[@#]\d+)(?=\s|[\p{P}]|$)/gu, '')
        : main_text; // If not a string, pass it through


    const hasFootnotes = Array.isArray(footnotes) && footnotes.length > 0;
    const hasLearningTips = typeof learning_tips === 'string' && learning_tips.trim() !== '';
    const hasSourceInfo = typeof source_info === 'string' && source_info.trim() !== '';

    return (
        <div className="poem-display-container">
            {/* Title */}
            {title && <h3 className="poem-title">{title}</h3>}

            {/* Author and Genre */}
            <p className="poem-meta">
                {author} {genre && `| ${genre}`}
            </p>

            {/* Main Text (CSS handles centering and font size) */}
            {main_text && (
                <pre className="poem-main-text">
                    {main_text}
                </pre>
            )}

            {/* Footnotes */}
            {hasFootnotes && (
                <div className="poem-footnotes">
                    <h4 className="poem-section-title">注释</h4>
                    <ul>
                        {footnotes.map((note, index) => (
                            <li key={index}>
                                {note.marker && <strong className="footnote-marker">〔{note.marker}〕</strong>}
                                {/* Render explanation, handling potential newlines if needed */}
                                {note.explanation || "无解释"}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Learning Tips */}
            {hasLearningTips && (
                 <div className="poem-learning-tips">
                    <h4 className="poem-section-title">学习提示</h4>
                    <p className="learning-tips-text">{learning_tips}</p>
                 </div>
            )}

             {/* Source Info */}
            {hasSourceInfo && (
                <div className="poem-source-info-container">
                    {/* Optional: Add title like <h4 className="poem-section-title">来源</h4> */}
                    <p className="source-info-text">{source_info}</p>
                </div>
            )}

            {/* Example of how to display boolean if true (currently hidden based on request) */}
            {/* {recitation_required && <p className="poem-boolean-info">要求背诵</p>} */}
            {/* {self_study && <p className="poem-boolean-info">适合自学</p>} */}
        </div>
    );
}


// --- App 組件 (Mostly Unchanged Logic) ---
function App() {
  // --- State Definitions (Unchanged) ---
  const [fullData, setFullData] = useState(null);
  const [chatHistory, setChatHistory] = useState([{ sender: 'ai', message: '我是基於課文數據訓練的Gemini AI，聊？', type: 'text', id: 'init' }]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [imageList, setImageList] = useState([]);
  const [imageListLoaded, setImageListLoaded] = useState(false);
  const [expandedBookIndex, setExpandedBookIndex] = useState(null);

  // --- Constants (Unchanged) ---
  const CLOUD_FLARE_WORKER_URL = "https://ai.bdfz.chat/";
  const MAX_CONTEXT_LENGTH = 7500;

  // --- Effects (Data Loading, Scrolling, Dark Mode - Unchanged) ---
  useEffect(() => {
    fetch('/data/all.json')
      .then(res => { if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`); return res.json(); })
      .then(d => {
        console.log("教材 JSON 加載成功");
        if (Array.isArray(d)) setFullData(d);
        else { console.error("加載的教材數據格式錯誤，應為數組。", d); setFullData([]); setChatHistory(p => [...p, { sender: 'ai', message: `加載核心教材數據格式錯誤。`, type: 'text', id: Date.now() + 'e_data_format' }]); }
      })
      .catch(e => { console.error('教材 JSON 加載失敗:', e); setFullData([]); setChatHistory(p => [...p, { sender: 'ai', message: `加載核心教材數據失敗: ${e.message}`, type: 'text', id: Date.now() + 'e_data_fetch' }]); });
  }, []);
  useEffect(() => {
    fetch('/image_list.json')
      .then(res => { if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`); return res.json(); })
      .then(d => { if (Array.isArray(d)) { setImageList(d); setImageListLoaded(true); console.log("圖片列表加載成功"); } else { console.error("圖片列表格式錯誤，應為數組"); setImageList([]); } })
      .catch(e => { console.error('圖片列表加載失敗:', e); setImageList([]); });
  }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatHistory]);
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) root.classList.add('dark-mode'); else root.classList.remove('dark-mode');
    const textColor = isDarkMode ? 'var(--dark-text-secondary)' : 'var(--ghibli-text-soft)';
    const titleColor = isDarkMode ? 'var(--dark-text-title)' : 'var(--ghibli-text-title)';
    const gridColor = isDarkMode ? 'var(--dark-chart-grid)' : 'var(--ghibli-border-color)';
    const tooltipBg = isDarkMode ? 'rgba(42, 58, 74, 0.9)' : 'rgba(90, 79, 74, 0.8)';
    const tooltipText = '#fff';
    // Update Chart.js defaults - check if ChartJS is already loaded
    if (ChartJS.defaults) {
        ChartJS.defaults.color = textColor;
        ChartJS.defaults.borderColor = gridColor;
        if (ChartJS.defaults.plugins?.title) ChartJS.defaults.plugins.title.color = titleColor;
        if (ChartJS.defaults.plugins?.legend?.labels) ChartJS.defaults.plugins.legend.labels.color = textColor;
        if (ChartJS.defaults.plugins?.tooltip) {
            ChartJS.defaults.plugins.tooltip.backgroundColor = tooltipBg;
            ChartJS.defaults.plugins.tooltip.titleColor = tooltipText;
            ChartJS.defaults.plugins.tooltip.bodyColor = tooltipText;
        }
        if (ChartJS.defaults.scale?.ticks) ChartJS.defaults.scale.ticks.color = textColor;
        if (ChartJS.defaults.scale?.grid) ChartJS.defaults.scale.grid.color = gridColor;
    } else {
        console.warn("ChartJS defaults could not be set, ChartJS might not be fully initialized yet.");
    }
  }, [isDarkMode]);

  // --- Sidebar Data Generation (Unchanged) ---
  const sidebarData = useMemo(() => {
    if (!fullData || !Array.isArray(fullData)) return null;
    console.log("Generating sidebar data for ALL books...");
    try {
      const sectionsMap = new Map();
      // This is the full function, no longer abbreviated
      const processContentArray = (contentArray) => {
          let items = [];
          if (!contentArray || !Array.isArray(contentArray)) return items;
          let processedTitlesInSection = new Set();

          contentArray.forEach(item => {
              if (!item) return;
              const title = String(item.title || item.book_title || item.activity_title || '').trim();
              if (!title || processedTitlesInSection.has(title)) return;
              processedTitlesInSection.add(title);

              const textNumber = String(item.text_number || '').trim();
              const author = String(item.author || '').trim();
              let displayParts = [];
              // Ensure '*' text_number is handled or ignored gracefully
              if (textNumber && /^\d+$/.test(textNumber)) {
                  displayParts.push(`${textNumber}.`);
              } else if (textNumber === '*') {
                  // Optional: Add a symbol or ignore for '*'
                  // displayParts.push('*');
              }
              displayParts.push(title);
              if (author) {
                  displayParts.push(`(${author})`);
              }
              items.push({
                  type: 'item',
                  display: displayParts.join(' '),
                  value: title, // ORIGINAL title for lookup
                  isClickable: true
              });
          });
          return items;
       };

      fullData.forEach((book, bookIndex) => {
          const bookIdentifier = String(book.metadata?.title || `Book ${bookIndex + 1}`).trim();
          // This is the full loop, no longer abbreviated
          if (book.table_of_contents && Array.isArray(book.table_of_contents)) {
              book.table_of_contents.forEach((unit, unitIndex) => {
                    if (!unit) return;
                    // Prefer unit_title, then unit_theme, then unit_number, then type, then fallback
                    let baseSectionTitleForDisplay = String(unit.unit_title || unit.unit_theme || '').trim();
                    const unitNumber = unit.unit_number;

                    if (!baseSectionTitleForDisplay && unitNumber) {
                        baseSectionTitleForDisplay = `第 ${unitNumber} 单元`;
                    } else if (!baseSectionTitleForDisplay) {
                       baseSectionTitleForDisplay = String(unit.type || `单元 ${unitIndex + 1}`).trim();
                    }

                    // Construct a more unique key/original title for the map
                    let originalTitleParts = [];
                    if (unitNumber) originalTitleParts.push(`第 ${unitNumber} 单元`);
                    if (unit.unit_title) originalTitleParts.push(unit.unit_title); // Use specific title if available
                    if (unit.unit_theme && unit.unit_title !== unit.unit_theme) originalTitleParts.push(unit.unit_theme); // Add theme if different

                    const originalPrefixedTitle = originalTitleParts.length > 0 ? originalTitleParts.join(' - ') : baseSectionTitleForDisplay;

                    const prefixedSectionKey = `${bookIdentifier} - ${originalPrefixedTitle}`;

                    // Only add if not already present (though Map should handle overwrite)
                    if (!sectionsMap.has(prefixedSectionKey)) {
                        sectionsMap.set(prefixedSectionKey, {
                            bookTitle: prefixedSectionKey, // Use this unique key
                            displayTitle: baseSectionTitleForDisplay, // User-friendly display
                            items: processContentArray(unit.content)
                        });
                    }
              });
          }
          // This is the full loop, no longer abbreviated
          if (book.supplementary_content && Array.isArray(book.supplementary_content)) {
              book.supplementary_content.forEach((supSection, supIndex) => {
                    if (!supSection) return;
                    const baseSectionTitle = String(supSection.section_title || `补充 ${supIndex + 1}`).trim();
                    const prefixedSectionKey = `${bookIdentifier} - ${baseSectionTitle}`;

                    // Only add if not already present
                    if (!sectionsMap.has(prefixedSectionKey)) {
                        sectionsMap.set(prefixedSectionKey, {
                            bookTitle: prefixedSectionKey,
                            displayTitle: baseSectionTitle,
                            items: processContentArray(supSection.items)
                        });
                    }
              });
          }
      });
      const finalSidebarData = Array.from(sectionsMap.values());
      console.log("Final sidebar data count (sections):", finalSidebarData.length);
      return finalSidebarData;
    } catch (error) {
      console.error("Error generating sidebar data:", error);
      setChatHistory(p => [...p, { sender: 'ai', message: `生成侧边栏目录时出错: ${error.message}`, type: 'text', id: Date.now() + 'e_sidebar_gen' }]);
      return [];
    }
  }, [fullData]);
  // --- End Sidebar Data Generation ---


  // --- 提取所有篇名和作者名 (Unchanged) ---
  const allTitles = useMemo(() => {
      if (!fullData || !Array.isArray(fullData)) return [];
      const titles = new Set();
      fullData.forEach(book => {
          book?.table_of_contents?.forEach(unit => {
              unit?.content?.forEach(item => {
                  if (item) {
                      if (item.title) titles.add(String(item.title).trim());
                      if (item.book_title) titles.add(String(item.book_title).trim());
                      if (item.activity_title) titles.add(String(item.activity_title).trim());
                  }
              });
               // Include unit titles if they exist
               if (unit && (unit.unit_title || unit.title)) {
                   titles.add(String(unit.unit_title || unit.title).trim());
               }
          });
          book?.supplementary_content?.forEach(sec => {
              sec?.items?.forEach(item => {
                   if (item && item.title) titles.add(String(item.title).trim());
                   if (item && item.book_title) titles.add(String(item.book_title).trim());
                   if (item && item.activity_title) titles.add(String(item.activity_title).trim());
              });
               // Include supplementary section titles
               if (sec && (sec.section_title || sec.title)) {
                  titles.add(String(sec.section_title || sec.title).trim());
               }
          });
           // Include book titles from metadata
           if (book && book.metadata?.title) {
               titles.add(String(book.metadata.title).trim());
           }
      });
      return Array.from(titles).filter(t => t);
    }, [fullData]);
  const allAuthors = useMemo(() => {
      if (!fullData || !Array.isArray(fullData)) return [];
      const authors = new Set();
      fullData.forEach(book => {
          book?.table_of_contents?.forEach(unit => {
              unit?.content?.forEach(item => item?.author && authors.add(String(item.author).trim()));
              if (unit?.author) authors.add(String(unit.author).trim());
          });
          book?.supplementary_content?.forEach(sec => {
             sec?.items?.forEach(item => item?.author && authors.add(String(item.author).trim()))
             if (sec?.author) authors.add(String(sec.author).trim());
          });
           // Include authors from metadata if present (add other relevant fields too)
           if (book?.metadata?.chief_editor) authors.add(String(book.metadata.chief_editor).trim());
           book?.metadata?.volume_editors?.forEach(ed => authors.add(String(ed).trim()));
           book?.metadata?.contributors?.forEach(co => authors.add(String(co).trim()));
      });
      return Array.from(authors).filter(a => a);
    }, [fullData]);
  // --- 列表提取結束 ---


  // --- Core Functions ---
  const formatText = (text) => {
      if (typeof text !== 'string') return '';
      return text.trim();
   };

  // --- sendPromptToAI (Unchanged) ---
  const sendPromptToAI = async (promptToSend, thinkingMessageIdToReplace) => {
      if (!promptToSend) {
          console.error("Attempted to send an empty prompt.");
           setChatHistory(prev => prev.map(msg =>
              msg.id === thinkingMessageIdToReplace
                  ? { sender: 'ai', message: "發生內部錯誤：嘗試發送空提示。", type: 'text', id: Date.now() + '_error_empty' }
                  : msg
            ));
          setLoading(false);
          return;
      }
      console.log("Sending prompt to AI (start):", promptToSend.substring(0, 500) + (promptToSend.length > 500 ? "..." : ""));
      setLoading(true);

      setChatHistory(prev => {
         const exists = prev.some(msg => msg.id === thinkingMessageIdToReplace && msg.isThinking);
         if (exists) {
             return prev; // Already exists, do nothing
         } else {
             console.warn(`Thinking message ID ${thinkingMessageIdToReplace} not found or not a thinking message, adding it now.`);
             const thinkingMessage = { sender: 'ai', message: <ThinkingIndicator />, isThinking: true, id: thinkingMessageIdToReplace };
             // Try to insert after the last user message, otherwise append
             const userMsgIndex = prev.findLastIndex(msg => msg.sender === 'user');
             if (userMsgIndex !== -1) {
                 const newHistory = [...prev];
                 // Find the correct insertion point AFTER potential static displays
                 let insertIndex = userMsgIndex + 1;
                 while (insertIndex < newHistory.length && newHistory[insertIndex].sender === 'ai' && !newHistory[insertIndex].isThinking) {
                     insertIndex++;
                 }
                 newHistory.splice(insertIndex, 0, thinkingMessage);
                 return newHistory;
             } else {
                 // If no user message found (unlikely), just append
                 return [...prev, thinkingMessage];
             }
         }
      });


      try {
          const response = await fetch(CLOUD_FLARE_WORKER_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ prompt: promptToSend }) });

          if (!response.ok) {
              let errorText = await response.text();
              let detail = `API Error ${response.status}`;
              try { const errJson = JSON.parse(errorText); detail += `: ${String(errJson.error || errJson.message || errorText).slice(0, 150)}`; if(errJson.source_api) detail += ` (Source API: ${errJson.source_api})`; } catch(e) { detail += `: ${errorText.slice(0, 150)}`; }
              detail += ` (ID: ${thinkingMessageIdToReplace})`; throw new Error(detail);
          }

          const result = await response.json();
          let aiReplyContent = typeof result.answer === 'string' ? result.answer : 'Hmm... AI 返回的不是文本內容呢。';
          if (!aiReplyContent.trim()) { aiReplyContent = 'Hmm... AI 好像沒有給出回答。'; }
          console.log("Received AI reply from API:", result.source_api || 'Unknown Source', "; Content Preview:", aiReplyContent.substring(0, 300) + (aiReplyContent.length > 300 ? "..." : ""));

          let aiMessageComponent; let messageType = 'text';

          if (typeof aiReplyContent === 'string') {
              const potentialTableData = parseMarkdownTable(aiReplyContent);
              if (potentialTableData) {
                  console.log("Detected Markdown Table");
                  messageType = 'table';
                  aiMessageComponent = <TableMessage tableData={potentialTableData} />;
              } else if (aiReplyContent.startsWith("CHART-LINE:")) {
                  console.log("Detected CHART-LINE");
                  messageType = 'chart';
                  try {
                      const chartJson = aiReplyContent.slice(11).trim();
                      if (chartJson.startsWith('{') && chartJson.endsWith('}')) {
                         aiMessageComponent = <ChartMessage type="line" chartData={JSON.parse(chartJson)} />;
                      } else { throw new Error("Invalid JSON structure for line chart"); }
                  } catch (e) {
                      console.error("Chart-Line JSON parse error:", e, "\nContent:", aiReplyContent.slice(11));
                      aiMessageComponent = <ReactMarkdown>{`圖表數據解析錯誤 (Line)... 原始指令:\n\`\`\`\n${aiReplyContent}\n\`\`\``}</ReactMarkdown>;
                      messageType = 'text';
                  }
              } else if (aiReplyContent.startsWith("CHART-BAR:")) {
                   console.log("Detected CHART-BAR");
                   messageType = 'chart';
                   try {
                       const chartJson = aiReplyContent.slice(10).trim();
                      if (chartJson.startsWith('{') && chartJson.endsWith('}')) {
                          aiMessageComponent = <ChartMessage type="bar" chartData={JSON.parse(chartJson)} />;
                      } else { throw new Error("Invalid JSON structure for bar chart"); }
                   } catch (e) {
                       console.error("Chart-Bar JSON parse error:", e, "\nContent:", aiReplyContent.slice(10));
                       aiMessageComponent = <ReactMarkdown>{`圖表數據解析錯誤 (Bar)... 原始指令:\n\`\`\`\n${aiReplyContent}\n\`\`\``}</ReactMarkdown>;
                       messageType = 'text';
                   }
              } else if (aiReplyContent.startsWith("TABLE:")) {
                   console.log("Detected TABLE: JSON");
                  messageType = 'table';
                  try {
                      const tableJson = aiReplyContent.slice(6).trim();
                       if (tableJson.startsWith('{') && tableJson.endsWith('}')) {
                           aiMessageComponent = <TableMessage tableData={JSON.parse(tableJson)} />;
                       } else { throw new Error("Invalid JSON structure for table"); }
                  } catch (e) {
                      console.error("Table JSON parse error:", e, "\nContent:", aiReplyContent.slice(6));
                      aiMessageComponent = <ReactMarkdown>{`表格數據解析錯誤 (JSON)... 原始指令:\n\`\`\`\n${aiReplyContent}\n\`\`\``}</ReactMarkdown>;
                      messageType = 'text';
                  }
              }
              else {
                  console.log("Rendering as Markdown Text");
                  // Don't remove markers here, markdown component should display them
                  // const cleanedReply = removeFootnoteMarkers(aiReplyContent);
                  aiMessageComponent = <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiReplyContent}</ReactMarkdown>;
                  messageType = 'text';
              }
          } else {
              console.warn("Non-string AI reply received (should have been handled earlier):", aiReplyContent);
              try {
                 // Display non-string as preformatted JSON
                 aiMessageComponent = <pre><code>{JSON.stringify(aiReplyContent, null, 2)}</code></pre>;
              } catch (e) {
                 // Fallback to string conversion
                 aiMessageComponent = String(aiReplyContent);
              }
              messageType = 'text'; // Treat as text display even if it's an object/array stringified
          }

          setChatHistory(prev => prev.map(msg => msg.id === thinkingMessageIdToReplace ? { sender: 'ai', message: aiMessageComponent, type: messageType, id: Date.now() + '_ai_' + messageType } : msg ));
      } catch (err) {
          console.error('AI fetch/process error:', err);
          setChatHistory(prev => prev.map(msg => msg.id === thinkingMessageIdToReplace ? { sender: 'ai', message: `Oops! AI 請求出錯了： ${err.message}`, type: 'text', id: Date.now() + '_error' } : msg ));
      } finally {
          setLoading(false);
      }
   };

  // --- Helper: Construct AI Prompt (Unchanged) ---
  const constructAIPrompt = (userQueryText, currentHistory, contextDataJsonString = null, contextDescription = '') => {
       const relevantHistoryForPrompt = currentHistory
            .filter(msg => !msg.isThinking && msg.id !== 'init' && msg.type !== 'component') // Exclude static component displays (like PoemDisplay)
            .slice(-10); // Limit context history length

       let historyString = "[對話歷史開始]\n";
       if (relevantHistoryForPrompt.length === 0) {
           historyString += "(无历史记录)\n";
       } else {
           relevantHistoryForPrompt.forEach(msg => {
               const prefix = msg.sender === 'user' ? "User:" : "AI:";
               let content = '[未知內容類型]';

               if (typeof msg.message === 'string') {
                    // Simple user message or basic AI text message
                    content = msg.message.substring(0, 500) + (msg.message.length > 500 ? "..." : "");
               } else if (React.isValidElement(msg.message)) {
                    // Handle React component messages passed to history
                    if (msg.message.type === ReactMarkdown && typeof msg.message.props.children === 'string') {
                         content = `[AI Markdown]: ${msg.message.props.children.substring(0, 480)}...`;
                    } else if (msg.message.type === TableMessage) {
                         content = '[AI 已發送表格數據]';
                    } else if (msg.message.type === ChartMessage) {
                         content = `[AI 已發送圖表數據 (${msg.message.props.type})]`;
                    } else if (msg.message.type === ThinkingIndicator) {
                         content = '[AI 正在思考...]'; // Should be filtered out, but handle defensively
                    }
                    // **Do not include PoemDisplay content in history for AI**
                    else if (msg.message.type === PoemDisplay) {
                         content = `[AI 已展示《${msg.message.props.data?.title || '未知標題'}》的詳細信息]`; // Inform AI it was shown, but don't send the HTML
                    }
                    else {
                         content = '[AI 發送了其他組件]'; // Catch-all for other components
                    }
               } else if (msg.type === 'image_display') {
                   content = '[AI 顯示了一張圖片]';
               } else if (msg.message && typeof msg.message === 'object') {
                   // This case might happen if an object slipped through without being a component
                   content = '[AI 發送了原始數據對象]';
               } else if (msg.message != null) { // Handle null/undefined defensively
                   content = String(msg.message).substring(0, 500) + (String(msg.message).length > 500 ? "..." : "");
               }
               historyString += `${prefix}\n${content}\n\n`;
           });
       }
       historyString += "[對話歷史結束]\n";

       let relevantDataSnippet = '';
       if (contextDataJsonString) {
          relevantDataSnippet = `
[與用戶问题/點擊相關的教材數據 JSON (可能因長度限制被截斷) 開始]
\`\`\`json
${contextDataJsonString}
\`\`\`
[與用戶问题/點擊相關的教材數據 JSON 結束]
`;
       }

        const systemPrompt = `
[系統指令開始]
你是一個聰明、有點調皮、像人類一樣的 AI 助手，擅長以輕鬆幽默且知識豐富的風格討論語文課文相關內容。你的知識主要來自關於語文教材的學習。
- 回答問題時，請運用你關於這些教材的知識。
- **重要：如果下面提供了 [相關的教材數據 JSON]，這表示用戶提到了具體的篇目或點擊了側邊欄的篇目。你必須優先基於這段提供的 JSON 信息來回答用戶的完整問題。你需要解析這個 JSON 結構來獲取信息（比如 'main_text', 'footnotes', 'learning_tips', 'author', 等字段）。可以直接引用、分析、總結其中的內容。注意 JSON 內容可能因過長被截斷。**
- 如果用戶的問題提到了作者（見 contextDescription），或者沒有提供相關的 JSON 數據片段，或者片段信息不足以回答，請結合你的通用知識庫和對話歷史進行回答，務必核查作者的維基生平，給出盡可能詳細的生平介紹和關鍵名言或有意思有意義的生平細節。
- 如果你的知識庫中確實沒有相關信息，可以根據常識回答，或者坦誠地說 '關於這個，我的學習資料裡好像沒有詳細記錄哦~' 或類似的話。
- 回答風格要自然、像和朋友聊天一樣，可以適當使用 Emoji (但不要過多)。
- 答案要清晰、結構化，適當時可以使用 Markdown 語法（例如列表、粗體、引用）。
- 不要透露你的知識來源細節（比如 JSON 文件），假裝這些知識你本來就知道，可以說“在我學過的課文裡...”。
- 避免重複 "根據我的資料庫..." 或類似的措辭。
- 當用戶問候或閒聊時，給予友好回應。
- **如果需要生成表格，請使用 Markdown 表格語法。**
- **如果需要生成圖表，請使用 "CHART-LINE: { ...JSON... }" 或 "CHART-BAR: { ...JSON... }" 的格式輸出，其中 {...JSON...} 是 Chart.js 需要的標準 data 對象結構 (包含 labels 和 datasets)。**
[系統指令結束]
`;

       const prompt = `
${systemPrompt}
${historyString}
${relevantDataSnippet}

[當前用戶完整問題/請求開始]
User:
${userQueryText}
[當前用戶完整問題/請求結束]

AI:
(請根據【系統指令】、【對話歷史】、用戶的【當前用戶完整問題/請求】以及上方可能的[相關教材數據 JSON]，生成回答。當前處理背景：${contextDescription || '用戶輸入了文本查詢。'})
`;
       return prompt;
    };


  // --- handleSend (Unchanged) ---
   const handleSend = async () => {
        const trimmedInput = userInput.trim();
        if (!trimmedInput || loading) {
            if (!trimmedInput) console.log("Empty input, not sending.");
            if (loading) console.log("Already loading, not sending.");
            return;
        }
        if (!fullData) {
            console.warn("fullData not loaded yet, cannot process message.");
            setChatHistory(prev => [...prev, { sender: 'user', message: formatText(trimmedInput), type: 'text', id: Date.now() + '_user_nodata' }, { sender: 'ai', message: "抱歉，教材數據還沒加載完成，請稍後再試~", type: 'text', id: Date.now() + '_ai_nodata_warn' }]);
             setUserInput('');
            return;
        }

        const newUserMessage = { sender: 'user', message: formatText(trimmedInput), type: 'text', id: Date.now() + '_user' };
        const thinkingMessageId = Date.now() + '_thinking';
        const thinkingMessage = { sender: 'ai', message: <ThinkingIndicator />, isThinking: true, id: thinkingMessageId };

        // Add user message and thinking indicator immediately
        const currentChatHistory = [...chatHistory, newUserMessage]; // History BEFORE thinking message is added for prompt context
        setChatHistory([...currentChatHistory, thinkingMessage]); // Update UI with both
        setUserInput('');


        // --- Context Finding Logic ---
        let contextDataJsonString = null;
        let contextDescriptionForAI = '';
        let primaryMatch = null;
        let queryCategory = null;

        const potentialMatches = [...allTitles, ...allAuthors]
            .filter(term => term && trimmedInput.includes(term))
            .sort((a, b) => b.length - a.length); // Prioritize longer matches

        if (potentialMatches.length > 0) {
            primaryMatch = potentialMatches[0];
            if (allTitles.includes(primaryMatch)) {
                queryCategory = 'titles';
                console.log(`Primary match (Title): "${primaryMatch}" in user input.`);
            } else if (allAuthors.includes(primaryMatch)) {
                queryCategory = 'authors';
                console.log(`Primary match (Author): "${primaryMatch}" in user input (Will not inject full JSON)`);
            } else {
                 // Match found but isn't in known titles or authors list (less likely with sort)
                 primaryMatch = null;
            }
        }

        // Prepare context based on match type
        if (primaryMatch && queryCategory === 'titles') {
            const foundItem = findTextData(fullData, 'titles', primaryMatch);
            if (foundItem) {
                try {
                    let itemString = JSON.stringify(foundItem);
                    if (itemString.length > MAX_CONTEXT_LENGTH) {
                        console.warn(`Context JSON for "${primaryMatch}" truncated (${itemString.length} > ${MAX_CONTEXT_LENGTH})`);
                        // Truncate carefully, trying to keep valid JSON structure if possible
                        // Simple substring might break it. Consider smarter truncation if needed.
                        itemString = itemString.substring(0, MAX_CONTEXT_LENGTH) + '...}'; // Indicate truncation
                    }
                    contextDataJsonString = itemString;
                    contextDescriptionForAI = `用戶的問題提到了篇目 "${primaryMatch}"。下面提供了與該篇目相關的教材數據（JSON格式，可能因長度限制被截斷）。請優先利用此數據回答用戶的完整問題。`;

                } catch (e) {
                    console.error(`Error stringifying found item for "${primaryMatch}":`, e);
                    contextDataJsonString = null; // Ensure it's null if stringify fails
                    contextDescriptionForAI = `用戶的問題提到了篇目 "${primaryMatch}"，但在準備相關數據時出錯。請基於你的通用知識回答。`;
                }
            } else {
                 console.log(`Title match found "${primaryMatch}", but findTextData returned null.`);
                 contextDescriptionForAI = `用戶的問題提到了篇目 "${primaryMatch}"，但在教材數據庫中查找詳細信息時未找到對應條目。請基於你的通用知識回答。`;
            }
        } else if (primaryMatch && queryCategory === 'authors') {
             contextDescriptionForAI = `用戶的問題提到了作者 "${primaryMatch}"。請基於你關於這位作者及其作品的知識進行回答。`;
             contextDataJsonString = null; // No JSON needed for author-only query
        }
        else {
            console.log("No known title or author keywords matched in input.");
            contextDescriptionForAI = `用戶的問題是：“${trimmedInput}”。未能直接匹配到已知的教材篇名或作者關鍵詞。請基於你的通用知識和對話歷史回答。`;
            contextDataJsonString = null; // No specific context JSON
        }
        // --- End Context Finding ---

        // Construct the prompt using the helper function
        const prompt = constructAIPrompt(
            trimmedInput,
            currentChatHistory, // Pass history BEFORE the thinking indicator
            contextDataJsonString,
            contextDescriptionForAI
        );

        // Send the prompt to the AI
        await sendPromptToAI(prompt, thinkingMessageId);
    };


  // --- Sidebar Click Handlers ---
  const handleBookClick = (index) => {
    setExpandedBookIndex(prevIndex => (prevIndex === index ? null : index));
  };

  // --- handleTextClick (Unchanged) ---
  const handleTextClick = async (textTitleValue) => {
    if (loading || !fullData) {
        console.warn("Sidebar click ignored: AI loading or fullData not available.");
        return;
    }
    console.log(`Sidebar item clicked, displaying content for: '${textTitleValue}'`);

    // 1. Find Context Data FIRST
    const foundData = findTextData(fullData, 'titles', textTitleValue);

    // 2. Define the user's query based on the click (for AI prompt)
    const userQueryForAI = `请详细介绍一下《${textTitleValue}》。`;

    // 3. Create User Message object (what user sees)
    const userClickMessage = {
        sender: 'user',
        message: `了解《${textTitleValue}》`, // Shorter message for UI
        type: 'text',
        id: Date.now() + '_userclick_sidebar'
    };

    // 4. Prepare Thinking Indicator
    const thinkingMessageId = Date.now() + '_thinking_sidebar';
    const thinkingMessage = {
        sender: 'ai',
        message: <ThinkingIndicator />,
        isThinking: true,
        id: thinkingMessageId
    };

    // 5. Prepare messages to add to the UI immediately
    const messagesToAddForUI = [userClickMessage]; // Start with user message

    // 6. Add Formatted Content Display Message (using PoemDisplay)
    let contentDisplayMessage = null;
    if (foundData) {
        try {
            // Create the PoemDisplay element
            const poemDisplayElement = <PoemDisplay data={foundData} />;

            // Create the message object for UI display
            contentDisplayMessage = {
                sender: 'ai', // Displayed as if from AI, but it's static content
                message: poemDisplayElement,
                type: 'component', // Use component type for React elements
                id: Date.now() + '_poem_display',
            };
            messagesToAddForUI.push(contentDisplayMessage); // Add formatted display message
            console.log("Found item data, adding formatted PoemDisplay message for UI.");
        } catch (e) {
             console.error("Error creating PoemDisplay component for:", textTitleValue, e);
             // Add an error message to UI
             const errorDisplayMessage = {
                 sender: 'ai',
                 message: `抱歉，顯示《${textTitleValue}》的詳細內容時出錯。`,
                 type: 'text',
                 id: Date.now() + '_display_error',
             };
             messagesToAddForUI.push(errorDisplayMessage);
        }
    } else {
        console.log(`No full data object found for sidebar click "${textTitleValue}" for static display.`);
         // Add a message indicating data wasn't found
         const notFoundMessage = {
             sender: 'ai',
             message: `抱歉，沒有在數據庫中找到《${textTitleValue}》的詳細數據。`,
             type: 'text',
             id: Date.now() + '_data_notfound',
         };
         messagesToAddForUI.push(notFoundMessage);
    }

    // 7. Add thinking indicator AFTER the static content display (or error/not found message)
    messagesToAddForUI.push(thinkingMessage);

    // 8. Update chat history for the UI with all prepared messages
    //    Capture the state *before* this update for the AI prompt history
    const historyBeforeUpdate = [...chatHistory];
    setChatHistory(prev => [...prev, ...messagesToAddForUI]);

    // 9. Prepare history context *for the AI prompt*
    //    Includes the user's click message, but excludes the static PoemDisplay and thinking indicator
    const historyForPrompt = [...historyBeforeUpdate, userClickMessage];

    // 10. Prepare context JSON and description for AI (logic remains the same)
    let contextDataJsonString = null;
    let contextDescriptionForAI = '';
    if (foundData) {
        try {
            // Still pass the potentially truncated JSON to the AI for its internal use
            let itemString = JSON.stringify(foundData);
            if (itemString.length > MAX_CONTEXT_LENGTH) {
                console.warn(`Context JSON for sidebar click "${textTitleValue}" truncated for AI (${itemString.length} > ${MAX_CONTEXT_LENGTH})`);
                itemString = itemString.substring(0, MAX_CONTEXT_LENGTH) + '...}'; // Indicate truncation
            }
            contextDataJsonString = itemString;
            contextDescriptionForAI = `用戶通過點擊側邊欄請求介紹篇目《${textTitleValue}》。下面提供了與該篇目相關的教材數據（JSON格式，可能因長度限制被截斷）。請優先利用此數據回答用戶的請求：“${userQueryForAI}”。注意：用戶已經看到了格式化後的文本內容，你的回答應侧重于分析、总结或回答关于文本的问题，而不是重复显示文本本身。`;
        } catch (e) {
            console.error(`Error stringifying found item for sidebar click "${textTitleValue}" for AI context:`, e);
            contextDataJsonString = null;
            contextDescriptionForAI = `用戶通過點擊側邊欄請求介紹篇目《${textTitleValue}》，但在準備相關數據給 AI 時出錯。請基於你的通用知識回答用戶的請求：“${userQueryForAI}”。`;
        }
    } else {
        console.log(`No full data object found for sidebar click "${textTitleValue}" for AI context.`);
        contextDataJsonString = null;
        contextDescriptionForAI = `用戶通過點擊側邊欄請求介紹篇目《${textTitleValue}》，但在教材數據庫中查找詳細信息時未找到對應條目。請基於你的通用知識回答用戶的請求：“${userQueryForAI}”。`;
    }

    // 11. Construct AI Prompt using the correct history and query
    const promptForAI = constructAIPrompt(
        userQueryForAI,       // The AI's task: "请详细介绍..."
        historyForPrompt,     // History *only* including the user message
        contextDataJsonString,// Potentially truncated JSON for AI's analysis
        contextDescriptionForAI
    );

    // 12. Send to AI, targeting the thinking indicator for replacement
    await sendPromptToAI(promptForAI, thinkingMessageId);
  };


  // --- handleKeyDown, handleShowRandomImage, toggleDarkMode (Unchanged) ---
  const handleKeyDown = (e) => {
      // Check if the textarea is focused before handling Enter key
      if (document.activeElement === e.target && e.key === 'Enter' && !e.shiftKey && !loading) {
          e.preventDefault(); // Prevent default newline behavior
          handleSend();
      }
  };

  const handleShowRandomImage = () => {
      console.log("Random image button clicked.");
      if (!imageListLoaded || imageList.length === 0) {
           console.warn("Cannot show random image: List not loaded or empty.");
           setChatHistory(p=>[...p,{sender:'ai',message:'抱歉，圖片列表還沒準備好哦~',type:'text',id:Date.now()+'w_img_load'}]);
           return;
      }
      if (loading) {
           console.warn("Cannot show random image: AI is currently processing.");
           return; // Optionally disable button instead
      }
      const randomIndex = Math.floor(Math.random() * imageList.length);
      const randomImageName = imageList[randomIndex];
      const imageUrl = `/images/textbook_pics/${randomImageName}`;
      console.log("Showing random image:", imageUrl);

       // Add a user-like message to indicate the action
      const userImageRequestMsg = {
           sender: 'user',
           message: `來張隨機圖片看看~`,
           type: 'text',
           id: Date.now() + '_user_req_img'
      };

      const newImageMessage = {
           sender: 'ai',
           type: 'image_display', // Specific type for image rendering logic
           imageUrl: imageUrl,
           imageAlt: `隨機教材圖片: ${randomImageName}`, // Add Alt Text
           id: Date.now() + '_random_img'
       };
      // Add both user request and AI image response
      setChatHistory(prev => [...prev, userImageRequestMsg, newImageMessage]);
  };

  const toggleDarkMode = () => {
      setIsDarkMode(prevMode => !prevMode);
  };

  // --- Render Logic (Structure unchanged, handles new 'component' and 'image_display' types) ---
  return (
    <div className={`container ${isDarkMode ? 'dark-mode-container-class' : ''}`}>
      {/* --- Sidebar Panel --- */}
      <div className="sidebar-panel">
        <h2 className="sidebar-header">教材目录</h2>
        {sidebarData ? (
            <Sidebar
              data={sidebarData}
              expandedIndex={expandedBookIndex}
              onBookClick={handleBookClick}
              onTextClick={handleTextClick} // Pass the updated handler
            />
         ) : (
            <div className="sidebar-loading">加载目录中...</div>
         )}
      </div>

      {/* --- Chat Panel --- */}
      <div className="chat-panel">
        <div className="chat-header">
           <span className="header-left-space"></span>
           <span className="header-title">
             課文 with AI / <a href="https://suen.bdfz.chat/posts/aiariticles/" target="_blank" rel="noopener noreferrer">by suen</a>
           </span>
           <button onClick={toggleDarkMode} className="dark-mode-toggle" title="切換主題">
              {isDarkMode ? '☀️' : '🌓'}
           </button>
        </div>
        <div id="chat-messages" className="chat-messages">
          {chatHistory.map((msg) => (
             <div
                key={msg.id}
                className={`message ${msg.sender === 'user' ? 'user-message' : 'ai-message'} ${msg.isThinking ? 'thinking-bubble' : ''} ${msg.type === 'component' || msg.type === 'table' || msg.type === 'chart' || msg.type === 'image_display' ? 'component-message' : ''}`}
             >
                {
                  (msg.sender === 'user' && typeof msg.message === 'string')
                    ? msg.message.split('\n').map((line, i, arr) => (
                        // Render user text line by line in <p> tags
                        <p key={i} style={{ margin: i < arr.length -1 ? '0 0 0.3em 0' : 0, minHeight: '1.2em' }}>
                            {line || <> </>} {/* Render non-breaking space for empty lines */}
                        </p>
                      ))
                  : (msg.sender === 'ai') ? (
                      (msg.isThinking) ? msg.message // Render ThinkingIndicator directly
                      : React.isValidElement(msg.message) ? msg.message // Handles Markdown, Table, Chart, PoemDisplay, etc.
                      : (msg.type === 'image_display' && msg.imageUrl) ? (
                           // Render image for 'image_display' type
                           <div>
                             <img src={msg.imageUrl} alt={msg.imageAlt || "隨機教材圖片"} loading="lazy" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', marginTop: '5px', display: 'block' }} />
                           </div>
                       )
                       : (typeof msg.message === 'string') ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.message}</ReactMarkdown> // Fallback: render AI string as Markdown
                       : String(msg.message) // Ultimate fallback: convert to string
                  )
                  : String(msg.message) // Fallback for non-string user messages (should not happen)
                }
             </div>
          ))}
          <div ref={chatEndRef} style={{ height: '1px' }} /> {/* Scroll anchor */}
        </div>
        <div className="chat-input">
           <button id="random-image-button" className="random-image-button" onClick={handleShowRandomImage} disabled={!imageListLoaded || imageList.length === 0 || loading} title="隨機顯示一張教材圖片">🖼️</button>
           <textarea id="user-input" placeholder="请输入你的问题 (按 Enter 发送)..." value={userInput} onChange={e => setUserInput(e.target.value)} onKeyDown={handleKeyDown} disabled={loading} rows={1} />
           <button id="send-button" className="send-button" onClick={handleSend} disabled={loading || !userInput.trim()} > {loading ? '...' : '发送'} </button>
        </div>
      </div>
    </div>
  );
}

export default App;